import React, { useMemo, useState } from "react";
import { Box, Typography, Button, Drawer } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { MimshakPensionService } from "../../services/mimshakPensionService";
import BeneficiariesTable from "./BeneficiariesTable";
import { BeneficiariesMetrics } from "./BeneficiariesMetrics";
import { calculateBeneficiariesMetrics } from "../../services/beneficiariesMetricsService";

interface BeneficiariesMetricsWithDrawerProps {
  mimshakData: MimshakData[];
  viewedType?: "USER" | "FAMILY";
}

export const BeneficiariesMetricsWithDrawer: React.FC<BeneficiariesMetricsWithDrawerProps> = ({
  mimshakData,
  viewedType,
}) => {
  const [beneficiariesDrawerOpen, setBeneficiariesDrawerOpen] = useState(false);

  // קבלת רשימת מוטבים ייחודיים (מסננים יורשים חוקיים)
  const distinctBeneficiaries = useMemo(() => {
    const products = mimshakData.flatMap(item => item.data?.products || []);
    const allBeneficiaries = MimshakPensionService.getBeneficiaryInfoDistinct(products);
    // סינון מוטבים שהם לא יורשים חוקיים (3 = LEGAL_HEIRS)
    return allBeneficiaries.filter(b => b.identification_type !== 3);
  }, [mimshakData]);

  // חישוב מטריקות באמצעות הפונקציה המרכזית
  const metrics = useMemo(() => {
    return calculateBeneficiariesMetrics(mimshakData, distinctBeneficiaries);
  }, [mimshakData, distinctBeneficiaries]);

  if (distinctBeneficiaries.length === 0 && metrics.totalLegalHeirs === 0 && metrics.totalWithoutBeneficiaries === 0 && metrics.totalWithoutBeneficiariesDeathCoverage === 0) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", p: 2, direction: "rtl" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" sx={{ textAlign: "right" }}>
          מוטבים למקרה מוות
        </Typography>
        <Button
          variant="contained"
          startIcon={<PeopleIcon sx={{ ml: 1 }} />}
          onClick={() => setBeneficiariesDrawerOpen(true)}
        >
          בדיקת מוטבים
        </Button>
      </Box>

      {/* מטריקות */}
      <BeneficiariesMetrics
        distinctBeneficiaries={distinctBeneficiaries}
        beneficiaryTotals={metrics.beneficiaryTotals}
        beneficiaryDeathCoverageTotals={metrics.beneficiaryDeathCoverageTotals}
        totalWithoutBeneficiaries={metrics.totalWithoutBeneficiaries}
        totalWithoutBeneficiariesDeathCoverage={metrics.totalWithoutBeneficiariesDeathCoverage}
        totalLegalHeirs={metrics.totalLegalHeirs}
        totalLegalHeirsDeathCoverage={metrics.totalLegalHeirsDeathCoverage}
      />

      {/* מגירה עם טבלת מוטבים */}
      <Drawer
        anchor="bottom"
        open={beneficiariesDrawerOpen}
        onClose={() => setBeneficiariesDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            height: '80vh',
            direction: 'rtl'
          }
        }}
      >
        {beneficiariesDrawerOpen && (
          <BeneficiariesTable
            mimshakData={mimshakData}
            viewedType={viewedType}
          />
        )}
      </Drawer>
    </Box>
  );
};
